<script setup>
import { ref, computed, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import MentionBox from '../mentions/MentionBox.vue';
import CannedResponseFilter from './CannedResponseFilter.vue';
import { debounce } from '@chatwoot/utils';

const props = defineProps({
  searchKey: {
    type: String,
    default: '',
  },
  filterTag: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['replace']);

const store = useStore();

// --- Store getters ---
const cannedMessages = useMapGetter('getCannedResponses');

// --- Computed ---
const items = computed(() =>
  cannedMessages.value.map(cannedMessage => ({
    label: cannedMessage.short_code,
    key: cannedMessage.short_code,
    description: cannedMessage.content,
  }))
);

// --- Filter state (managed by CannedResponseFilter) ---
const filterState = ref({ filterType: 'accessible', teamIds: [] });

// --- Hash tag aliases (multi-language support) ---
const TAG_ALIASES = {
  all: ['all', 'все', 'всі'],
  global: ['global', 'глобал', 'глобальные', 'глобальні'],
};

// --- Sync filterTag prop (from #hashtag in editor) into filterState ---
const syncFilterTagToState = tag => {
  const normalized = tag.toLowerCase().trim();

  if (TAG_ALIASES.all.includes(normalized)) {
    filterState.value = { filterType: 'all', teamIds: [] };
  } else if (TAG_ALIASES.global.includes(normalized)) {
    filterState.value = { filterType: 'global', teamIds: [] };
  } else if (tag !== 'teams') {
    // No hashtag or unknown → reset to default (accessible for agent)
    filterState.value = { filterType: 'accessible', teamIds: [] };
  }
  // 'teams' is only set via UI, not via #hashtag in text
};

// --- Methods ---
const fetchCannedResponses = debounce(() => {
  const params = { searchKey: props.searchKey };

  if (filterState.value.filterType === 'all') {
    params.filter = 'all';
  } else if (filterState.value.filterType === 'global') {
    params.filter = 'global';
  } else if (filterState.value.filterType === 'teams') {
    params.teamIds = filterState.value.teamIds;
  }
  // 'accessible' → default, no filter params sent

  store.dispatch('getCannedResponse', params);
}, 300);

const handleMentionClick = (item = {}) => {
  emit('replace', item.description);
};

// --- Watchers ---
watch(
  () => props.searchKey,
  () => {
    fetchCannedResponses();
  }
);

watch(
  () => props.filterTag,
  newTag => {
    syncFilterTagToState(newTag);
    // fetchCannedResponses will be triggered by the filterState watcher below
  }
);

watch(
  () => filterState.value,
  () => {
    fetchCannedResponses();
  },
  { deep: true }
);

// --- Init ---
syncFilterTagToState(props.filterTag);
fetchCannedResponses();
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div class="relative">
    <MentionBox
      v-if="items.length"
      :items="items"
      @mention-select="handleMentionClick"
    >
      <template #header>
        <CannedResponseFilter v-model="filterState" />
      </template>
    </MentionBox>
  </div>
</template>
